import Decimal from 'decimal.js';
import { Product } from '../../core';
import { CustomFields, Expense, ListTemplate, Note, Organization, Place, ProductionRunComponent } from '../../types';
import { AddressComponent, AddressTemplateType } from '../components/address.template';
import { documentNotes } from '../components/documentNotes.template';
import { ExpensesComponent, ExpenseTemplateDataType } from '../components/expensesGrid.template';
import { OrganizationComponent } from '../components/organization.template';
import { DocumentTypePopulator, TemplateComponent } from '../DocumentGenerator';
import { applyListMeta, applyTableMetadata, formatDate, formatDecimal } from './formatters';

type ProductionRunComponentTemplateType = ListTemplate<{
  product?: string;
  commodity?: string;
  variety?: string;
  size?: string;
  unit?: string;
  label?: string;
  grade?: string;
  organic?: boolean;
  lot?: string;
  weight?: string;
  quantityChange?: string;
  weightDumped?: string;
}>;

export type ProductionRunInputType = {
  id: number;

  slug: string;
  status: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETE';
  startedAt?: string | Date;
  completedAt?: string | Date;

  commodity?: { name: string };
  countryOfOrigin?: string;
  organic?: boolean;

  warehouse?: Place;
  productionRunComponents: Array<ProductionRunComponent>;
  expenses: Expense[];

  notes: Note[];
  customFields: CustomFields;
  organization: Organization;
};

export type ProductionRunTemplateDataType = Partial<{
  runNumber: string;
  status: string;
  startedAt: string;
  completedAt: string;
  commodity: string;
  countryOfOrigin: string;
  organic: string;

  inputs: ProductionRunComponentTemplateType;
  outputs: ProductionRunComponentTemplateType;

  charges: ExpenseTemplateDataType;
  chargesSubtotal: string;

  totalInputWeight: string;
  totalOutputWeight: string;
  percentUtilized: string;
  weightCulled: string;
  percentWeightCulled: string;

  customFields: CustomFields;
  notes: ListTemplate<{ text: string }>;

  warehouse: AddressTemplateType;
}>;

const fromDecimal = (value?: number | Decimal) => {
  if (!value) {
    return 0;
  }

  return new Decimal(value).toNumber();
};

export const getComponentWeight = (component: ProductionRunComponent, showInitial?: boolean) => {
  // only output components may have weightProduced
  if (component.type === 'OUTPUT') {
    if (component.weightProduced !== null) {
      return fromDecimal(component.weightProduced);
    }

    return Product.Weight.of({
      variableWeight: component.lot.product.variableWeight,
      weightPerInventoryUnit: fromDecimal(component.lot.product.weightPerInventoryUnit),
    })
      .withLot(component.lot)
      .withPalletTags(component.lot.palletTags!)
      .forQuantity(Math.abs(component.quantityChange));
  }

  const qtyChange = Math.abs(component.quantityChange);
  const palletTagWeight = component.palletTagInputs.reduce((acc, input) => {
    return acc + (input.weight ?? 0);
  }, 0);
  const palletTagQuantity = component.palletTagInputs.reduce((acc, { quantity }) => acc + quantity, 0);

  // [TAS-3136] Over-allocated pallet tags: cap Weight Used at the consumed quantity's proportion of
  // the allocated pallet weight (never report more than what was consumed). Calls the same shared
  // helper as the api read path, so printed/exported documents cannot drift from the UI.
  if (palletTagQuantity > qtyChange) {
    const cappedWeight = Product.Weight.cappedPalletTagWeight(palletTagWeight, qtyChange, palletTagQuantity);

    if (Number.isFinite(cappedWeight) && cappedWeight > 0) {
      return cappedWeight;
    }
    // The allocation carries no weight to cap (every palletTagInput.weight is null), so there is
    // nothing for TAS-3136 to correct — fall through to the TAS-2706 proportional draw-down below.
  }

  // capping to 6 because the values deviate a bit at higher levels of precision.
  const nonPalletTagQuantity = +(qtyChange - palletTagQuantity).toFixed(6);

  let weight = Product.Weight.of({
    variableWeight: component.lot.product.variableWeight,
    weightPerInventoryUnit: fromDecimal(component.lot.product.weightPerInventoryUnit),
  })
    .withLot(component.lot)
    .withPalletTags(component.lot.palletTags!);

  let lotQuantity = component.lot.quantity;

  if (showInitial) {
    weight = weight.asInitialWeight();

    lotQuantity = component.lot.initialQuantity;
  }

  const averageLotWeight = weight.averageWeightForQuantity(lotQuantity - palletTagQuantity);

  const nonPalletTagWeight = averageLotWeight * nonPalletTagQuantity;

  const resultWeight = palletTagWeight + nonPalletTagWeight;

  // TAS-2706: when no strategy yields a weight, draw it down proportionally so the
  // document weight tracks quantity (shared with computeInputComponentWeight).
  if (!Number.isFinite(resultWeight) || resultWeight <= 0) {
    const fallbackLotWeight = showInitial ? component.lot.initialWeight : component.lot.weight;
    const fallbackLotQuantity = showInitial ? component.lot.initialQuantity : component.lot.quantity;

    if (fallbackLotWeight && fallbackLotQuantity) {
      return Product.Weight.proportionalDrawDown(fromDecimal(fallbackLotWeight), fallbackLotQuantity, qtyChange);
    }
  }

  return Number.isNaN(resultWeight) ? 0 : resultWeight;
};

const ProductionRunComponentsComponent: TemplateComponent<
  Array<ProductionRunComponent>,
  ProductionRunComponentTemplateType,
  { totalWeight: number; isCompleted: boolean; productDescriptionTemplate?: string }
> = {
  templatize: (
    components: Array<ProductionRunComponent>,
    { totalWeight, isCompleted, productDescriptionTemplate }
  ): ProductionRunComponentTemplateType => {
    return applyTableMetadata(
      components.map((component) => {
        const weight = getComponentWeight(component, isCompleted) ?? 0;

        return {
          type: component.type,
          product: Product.productDescription(component.lot?.product, productDescriptionTemplate),
          commodity: component.lot?.product?.commodity?.name,
          variety: component.lot?.product?.commodityStyle?.name,
          size: component.lot?.product?.commoditySize?.name,
          unit: component.lot?.product?.commodityUnit?.name,
          label: component.lot?.product?.commodityLabel?.name,
          grade: component.lot?.product?.grade,
          organic: component.lot?.product?.organic,
          lot: component.lot?.slug,
          weight: formatDecimal(weight),
          quantity: formatDecimal(Math.abs(component.quantityChange)),
          weightDumped: formatDecimal(component.weightDumped),
          percentWeight: `${((weight / totalWeight) * 100).toFixed(2)}%`,
        };
      })
    );
  },
  definitionMap: {
    product: { label: 'Product', type: 'string' },
    commodity: { label: 'Commodity', type: 'string' },
    variety: { label: 'Variety', type: 'string' },
    size: { label: 'Size', type: 'string' },
    unit: { label: 'Unit', type: 'string' },
    label: { label: 'Label', type: 'string' },
    grade: { label: 'Grade', type: 'string' },
    organic: { label: 'Organic', type: 'boolean' },
    lot: { label: 'Lot', type: 'string' },
    weight: { label: 'Weight', type: 'string' },
    quantityChange: { label: 'Quantity Change', type: 'string' },
    weightDumped: { label: 'Weight Dumped', type: 'string' },
  },
};

export const ProductionRunTemplate: DocumentTypePopulator<
  ProductionRunInputType,
  { productDescriptionTemplate?: string },
  ProductionRunTemplateDataType
> = {
  populate: (productionRun, { type, templateId, productDescriptionTemplate }, globalOptions) => {
    if (!productionRun) return {};

    const { productionRunComponents, organization } = productionRun;

    const inputComponents = productionRunComponents.filter((c) => c.type === 'INPUT');
    const outputComponents = productionRunComponents.filter((c) => c.type === 'OUTPUT');
    const primaryBusiness = organization?.places?.[0];
    const isCompleted = productionRun.status === 'COMPLETE';

    const totalInputWeight = inputComponents.reduce((acc, component) => {
      return acc + (getComponentWeight(component, isCompleted) ?? 0);
    }, 0);

    const totalOutputWeight = outputComponents.reduce((acc, component) => {
      return acc + (getComponentWeight(component, isCompleted) ?? 0);
    }, 0);

    const weightCulled = inputComponents.reduce((acc, component) => {
      return acc + (fromDecimal(component.weightDumped) ?? 0);
    }, 0);

    const chargesSubtotal = productionRun.expenses.reduce((acc, expense) => {
      return acc + expense.unitAmount * expense.quantity;
    }, 0);

    const quantitiesByUnit = (components: ProductionRunComponent[]) =>
      applyListMeta(
        Object.entries(
          components.reduce<{ [unit: string]: number }>((acc, component) => {
            const quantity = Math.abs(component.quantityChange);
            const unit = component.lot.product.commodityUnit.name;

            if (!acc[unit]) {
              acc[unit] = 0;
            }

            acc[unit] += quantity;

            return acc;
          }, {})
        ).map(([unit, quantity]) => {
          return { unit, quantity };
        })
      );

    const weightUtilized = totalOutputWeight / (totalInputWeight - weightCulled);

    return {
      runNumber: productionRun.slug,
      orgLogo: organization?.logoUrl,
      organization: OrganizationComponent.templatize({ organization, customOrgName: globalOptions.customOrgName }),
      businessAddress: AddressComponent.templatize(primaryBusiness),
      phoneNumber: primaryBusiness?.phone,

      status: productionRun.status,
      warehouse: AddressComponent.templatize(productionRun.warehouse),
      startedAt: formatDate(productionRun.startedAt),
      completedAt: formatDate(productionRun.completedAt),

      // Commodity
      commodity: productionRun.commodity?.name,
      countryOfOrigin: productionRun.countryOfOrigin,
      organic: productionRun.organic ? 'Yes' : 'No',

      inputs: ProductionRunComponentsComponent.templatize(inputComponents, {
        totalWeight: totalInputWeight,
        isCompleted,
        productDescriptionTemplate: productDescriptionTemplate ?? organization.productDescriptionTemplate,
      }),
      inputQuantities: quantitiesByUnit(inputComponents),
      outputs: ProductionRunComponentsComponent.templatize(outputComponents, {
        totalWeight: totalOutputWeight,
        isCompleted,
        productDescriptionTemplate: productDescriptionTemplate ?? organization.productDescriptionTemplate,
      }),
      outputQuantities: quantitiesByUnit(outputComponents),
      totalInputWeight: formatDecimal(totalInputWeight),
      totalOutputWeight: formatDecimal(totalOutputWeight),
      percentUtilized: `${(weightUtilized * 100).toFixed(2)}%`,
      weightCulled: formatDecimal(weightCulled),
      percentWeightCulled: `${((weightCulled / totalInputWeight) * 100).toFixed(2)}%`,

      charges: ExpensesComponent.templatize(productionRun.expenses, { currency: 'USD' }),
      chargesSubtotal: formatDecimal(chargesSubtotal),

      notes: documentNotes(productionRun.notes, templateId, type),
      customFields: productionRun.customFields,
    };
  },

  definitionMap: {
    runNumber: { label: 'Production Run Number', type: 'string' },
    status: { label: 'Status', type: 'string' },
    warehouse: { label: 'Location', type: 'address', children: AddressComponent.definitionMap },
    startedAt: { label: 'Started At', type: 'string' },
    completedAt: { label: 'Completed At', type: 'string' },
    commodity: { label: 'Commodity', type: 'string' },
    countryOfOrigin: { label: 'Country of Origin', type: 'string' },
    organic: { label: 'Organic', type: 'string' },
    customFields: { label: 'Custom Fields', type: 'customFields', root: 'productionRun' },
    notes: { label: 'Notes', type: 'list', children: { text: { label: 'Text', type: 'string' } } },
    inputs: { label: 'Input Components', type: 'table', children: ProductionRunComponentsComponent.definitionMap },
    outputs: { label: 'Output Components', type: 'table', children: ProductionRunComponentsComponent.definitionMap },
    totalInputWeight: { label: 'Total Input Weight', type: 'string' },
    totalOutputWeight: { label: 'Total Output Weight', type: 'string' },
    percentUtilized: { label: 'Percent Utilized', type: 'string' },
    weightCulled: { label: 'Weight Culled', type: 'string' },
    percentWeightCulled: { label: 'Percent Weight Culled', type: 'string' },
    charges: { label: 'Charges', type: 'table', children: ExpensesComponent.definitionMap },
    chargesSubtotal: { label: 'Charges Subtotal', type: 'string' },
  },
};
